import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { EditOutlined, MoreVertOutlined, SubdirectoryArrowRightOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { GoodsReceipt } from "@/graphql/codegen/graphql"

interface CostBreakdownActionsProps {
  goodsReceipt: GoodsReceipt
  onEdit: (goodsReceipt: GoodsReceipt) => void
  onCostSourceChange?: (goodsReceipt: GoodsReceipt) => void
  testIdPrefix?: string
  isProductLevel?: boolean
}

export const CostBreakdownActions: FC<CostBreakdownActionsProps> = ({
  goodsReceipt,
  onEdit,
  onCostSourceChange,
  testIdPrefix = "cost-breakdown",
  isProductLevel = false,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleDropdownOpen = () => setIsDropdownOpen(true)

  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handleEdit = () => {
    handleDropdownClose()
    onEdit(goodsReceipt)
  }

  const handleCostSourceChange = () => {
    handleDropdownClose()
    onCostSourceChange?.(goodsReceipt)
  }

  const { isCostManagedInAs400, costBreakdown } = goodsReceipt
  const isProductCostCompleted = costBreakdown?.lineCostBreakdowns?.every((breakdown) => breakdown.isCompleted)
  const canChangeCostSource = isCostManagedInAs400 || !isProductCostCompleted

  return (
    <Box className='flex items-center' ref={anchorElRef}>
      <IconButton onClick={handleDropdownOpen} data-test={`${testIdPrefix}-row-actions-button`}>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        data-test={`${testIdPrefix}-row-actions-menu`}
      >
        <MenuItem data-test='edit-menu-item' disabled={isCostManagedInAs400} onClick={handleEdit}>
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          <ListItemText primary={isProductLevel ? "Add/Edit Product Breakdown" : "Add/Edit Cost Breakdown"} />
        </MenuItem>
        {onCostSourceChange && (
          <MenuItem
            data-test='change-cost-source-menu-item'
            disabled={!canChangeCostSource}
            onClick={handleCostSourceChange}
          >
            <ListItemIcon>
              <SubdirectoryArrowRightOutlined />
            </ListItemIcon>
            <ListItemText primary={isCostManagedInAs400 ? "Manage Cost in AEROS" : "Manage Cost in AS400"} />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
